// PQRSD Test Bench — suite catalog.
//
// Each test's run(ctx) exercises the REAL PQRSD module (live dev backend) and
// returns { assertions:[{label,ok,detail}], message } — or { status:"skipped",
// message } for features that can't be triggered purely client-side. The bench
// derives passed/failed from the assertions (any failing assertion => failed)
// and renders each row with its assertions expandable.
//
// ctx = {
//   user,            // redux state.user.value
//   perms,           // user.permissions (array of strings)
//   testEmailBase,   // tester email; alias variants keep mail in their inbox
//   register(rad),   // record a radicado for cleanup
//   getRegistered(), // -> radicado[]
//   state,           // shared mutable object for one Run pass (fixtures)
// }

import {
  PQRSD_TYPES,
  PQRSD_SLA_DAYS,
  PQRSD_STATUS,
  PQRSD_MAX_INTERIMS_PER_CYCLE,
  PQRSD_MAX_REOPENS,
  PQRSD_MAX_FILES,
  PQRSD_MAX_FILE_BYTES,
  isAllowedPqrsdMime,
  PERMISSIONS,
} from "../../constants";
import {
  listPqrsd,
  fetchPqrsdPage,
  getPqrsd,
  assignPqrsd,
  setPqrsdInProgress,
  closePqrsd,
  returnPqrsd,
  forwardPqrsd,
  reassignPqrsd,
  startResponseFlow,
  getPqrsdResponses,
  subscribePqrsdList,
  sendInfoRequest,
  withdrawInfoRequest,
  extendInfoRequest,
  decreeDesistimiento,
  listInfoRequests,
} from "../../services/backend/PqrsdService";
import { getReciversUser } from "../../services/backend/UserService";
import { getPqrsdConfig } from "../../services/backend/PqrsdConfigService";
import { availableAnswerTemplates } from "../pqrsd/responseChainDraft";
import {
  intakePqrsd,
  callPqrsdPublic,
  aliasEmail,
  makeTestFile,
  makeDisallowedFile,
  assert,
  assertEqual,
  assertThrowsAsync,
  callPqrsdInfoReply,
  tokenFromLink,
  forceSlaDeadline,
  LAB_TAG,
} from "./pqrsdLabHelpers";
import { addBusinessDays, diffBusinessDays } from "../../services/backend/gfunctions/pqrsd/_shared/businessDays";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// handlePqrsd writes the filed doc asynchronously after intake returns; retry a
// few times so the lifecycle tests don't race the write.
async function getPqrsdRetry(radicado, tries = 6, delay = 700) {
  for (let i = 0; i < tries; i++) {
    const found = await getPqrsd(radicado);
    if (found) return found;
    await sleep(delay);
  }
  return null;
}

const actorOf = (user) => ({
  userId: user?.userId || null,
  name: user?.name || "Lab Tester",
  userName: user?.name || "Lab Tester",
  email: user?.email || null,
});

// We assign/forward/reassign to the tester themself so the actions are valid
// without needing a second account or the receivers list.
const selfTarget = (user) => ({
  userId: user?.userId || null,
  name: user?.name || "Lab Tester",
  email: user?.email || null,
});

// Files a fresh PQRSD via the live intake CF and registers it for cleanup.
async function createFixture(ctx, { type = "queja", anonymous = false, tag } = {}) {
  const email = aliasEmail(ctx.testEmailBase, tag || `fx${Date.now()}`);
  const subject = `${LAB_TAG} ${PQRSD_TYPES[type] || type} ${new Date()
    .toISOString()
    .slice(11, 19)}`;
  const { ok, status, json } = await intakePqrsd({ type, anonymous, email, subject });
  if (!ok || !json?.radicado) {
    throw new Error(
      `Intake falló (HTTP ${status}): ${json?.error || "respuesta sin radicado"}`
    );
  }
  ctx.register(json.radicado);
  return { radicado: json.radicado, referenceId: json.referenceId, email, subject };
}

const eventTypes = (doc) =>
  (Array.isArray(doc?.events) ? doc.events : []).map((e) => e.eventType);

// Internal users available as PQRSD operators (for multi-assignee tests).
// Same INTERNAL_RECEIVER source the assign dialog uses; normalized defensively.
async function getInternalUsers(user) {
  try {
    const res = await getReciversUser(user?.token);
    const list = Array.isArray(res) ? res : res?.users || res?.result || [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

const uuid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `lab-${Date.now()}-${Math.random().toString(16).slice(2)}`;

// ===========================================================================
// SUITE A — Constants & pure helpers (no backend)
// ===========================================================================
const suiteConstants = {
  key: "constants",
  title: "A · Constantes y utilidades (sin backend)",
  tests: [
    {
      key: "types",
      name: "Tipos PQRSD (8)",
      description: "PQRSD_TYPES define los 8 tipos esperados con etiqueta.",
      run: async () => {
        const keys = Object.keys(PQRSD_TYPES);
        const expected = [
          "peticion_general",
          "consulta",
          "info",
          "queja",
          "reclamo",
          "denuncia",
          "sugerencia",
          "felicitacion",
        ];
        const assertions = [assertEqual("cantidad de tipos", keys.length, 8)];
        expected.forEach((k) =>
          assertions.push(
            assert(`tipo "${k}" presente con etiqueta`, !!PQRSD_TYPES[k], PQRSD_TYPES[k])
          )
        );
        return { assertions };
      },
    },
    {
      key: "sla",
      name: "SLA por tipo (días hábiles)",
      description: "PQRSD_SLA_DAYS coincide con la Ley 1755/2015.",
      run: async () => ({
        assertions: [
          assertEqual("consulta", PQRSD_SLA_DAYS.consulta, 30),
          assertEqual("info", PQRSD_SLA_DAYS.info, 10),
          assertEqual("queja", PQRSD_SLA_DAYS.queja, 15),
          assertEqual("peticion_general", PQRSD_SLA_DAYS.peticion_general, 15),
          assertEqual("reclamo", PQRSD_SLA_DAYS.reclamo, 15),
          assertEqual("denuncia", PQRSD_SLA_DAYS.denuncia, 15),
        ],
      }),
    },
    {
      key: "statuses",
      name: "Estados PQRSD (9)",
      description: "PQRSD_STATUS cubre todo el ciclo de vida.",
      run: async () => {
        const v = Object.values(PQRSD_STATUS);
        const need = [
          "radicado",
          "asignado",
          "en_tramite",
          "respondido",
          "cerrado",
          "devuelto",
          "reabierto",
          // Requerimiento de información al ciudadano (CPACA art. 17).
          "pendiente_info",
          "desistido",
          // Recurso de reposición against the desistimiento.
          "en_recurso",
        ];
        return {
          assertions: [
            assertEqual("cantidad de estados", v.length, 10),
            ...need.map((s) => assert(`estado "${s}"`, v.includes(s))),
          ],
        };
      },
    },
    {
      key: "caps",
      name: "Topes (interinos y reaperturas)",
      description: "Tope de respuestas interinas por ciclo y reaperturas ciudadanas.",
      run: async () => ({
        assertions: [
          assertEqual("interinos por ciclo", PQRSD_MAX_INTERIMS_PER_CYCLE, 2),
          assertEqual("reaperturas máximas", PQRSD_MAX_REOPENS, 5),
        ],
      }),
    },
    {
      key: "responses-sort",
      name: "getPqrsdResponses ordena por index",
      description: "El selector ordena el historial de respuestas ascendentemente.",
      run: async () => {
        const docu = {
          pqrsd: { responses: [{ index: 2 }, { index: 0 }, { index: 1 }] },
        };
        const sorted = getPqrsdResponses(docu).map((r) => r.index);
        return {
          assertions: [
            assert("orden ascendente", JSON.stringify(sorted) === "[0,1,2]", sorted.join(",")),
            assert("doc vacío => []", getPqrsdResponses({}).length === 0),
          ],
        };
      },
    },
    {
      key: "annex-limits",
      name: "Límites de anexos",
      description:
        "Constantes de adjuntos sincronizadas con intakePqrsd: 10 archivos, 10 MB, tipos permitidos.",
      run: async () => ({
        assertions: [
          assertEqual("máx. archivos", PQRSD_MAX_FILES, 10),
          assertEqual("máx. bytes por archivo", PQRSD_MAX_FILE_BYTES, 10 * 1024 * 1024),
          assert("PDF permitido", isAllowedPqrsdMime("application/pdf")),
          assert("imagen permitida", isAllowedPqrsdMime("image/png")),
          assert("Word permitido", isAllowedPqrsdMime("application/msword")),
          assert(
            "Excel (xlsx) permitido",
            isAllowedPqrsdMime("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
          ),
          assert("texto permitido", isAllowedPqrsdMime("text/plain")),
          assert("ZIP rechazado", !isAllowedPqrsdMime("application/zip")),
        ],
      }),
    },
  ],
};

// ===========================================================================
// SUITE B — Intake (live)
// ===========================================================================
const suiteIntake = {
  key: "intake",
  title: "B · Radicación / intake (backend en vivo)",
  tests: [
    {
      key: "all-types",
      name: "Radica los 8 tipos",
      description: "Envía un PQRSD de cada tipo y verifica radicado + referenceId.",
      run: async (ctx) => {
        const assertions = [];
        for (const type of Object.keys(PQRSD_TYPES)) {
          try {
            const fx = await createFixture(ctx, { type, tag: `type-${type}` });
            assertions.push(
              assert(`${type} → ${fx.radicado}`, !!fx.radicado, `ref ${fx.referenceId || "—"}`)
            );
          } catch (e) {
            assertions.push(assert(`${type}`, false, e.message));
          }
        }
        return { assertions, message: "8 radicaciones (consume ~8 del cupo IP/10min)" };
      },
    },
    {
      key: "anonymous",
      name: "Radicación anónima",
      description: "Un PQRSD anónimo se radica y es rastreable.",
      run: async (ctx) => {
        const fx = await createFixture(ctx, { type: "denuncia", anonymous: true, tag: "anon" });
        const track = await callPqrsdPublic({ radicado: fx.radicado, email: fx.email });
        return {
          assertions: [
            assert("radicado generado", !!fx.radicado, fx.radicado),
            assert("rastreo responde", track.ok && track.json?.result !== false),
            assert(
              "proyección coincide",
              track.json?.pqrsd?.radicado === fx.radicado,
              track.json?.pqrsd?.radicado
            ),
          ],
        };
      },
    },
    {
      key: "attachment",
      name: "Radicación con adjunto",
      description: "Sube un archivo en memoria y verifica que persiste.",
      run: async (ctx) => {
        const email = aliasEmail(ctx.testEmailBase, "att");
        const subject = `${LAB_TAG} adjunto ${Date.now()}`;
        const file = makeTestFile();
        const { ok, status, json } = await intakePqrsd({
          type: "info",
          email,
          subject,
          files: [file],
        });
        if (ok && json?.radicado) ctx.register(json.radicado);
        const docu = ok && json?.radicado ? await getPqrsdRetry(json.radicado) : null;
        // handlePqrsd stores annexes at the TOP-LEVEL `annexes` field (each entry
        // { url, id, fileHash, name, mimeType, size }), not pqrsd.attachments.
        const annexes = docu?.annexes || docu?.pqrsd?.attachments || docu?.attachments || [];
        return {
          assertions: [
            assert("intake ok", ok && !!json?.radicado, `HTTP ${status} ${json?.error || ""}`),
            assert("anexo persistido en annexes[] (≥1)", Array.isArray(annexes) && annexes.length >= 1, `n=${annexes.length}`),
            assert("anexo tiene url y nombre", !!annexes[0]?.url && !!annexes[0]?.name, annexes[0]?.name),
          ],
          data: { annexes },
        };
      },
    },
    {
      key: "disallowed-file",
      name: "Rechaza tipo de archivo no permitido",
      description: "Un adjunto .zip (MIME no permitido) es rechazado por intakePqrsd.",
      run: async (ctx) => {
        const email = aliasEmail(ctx.testEmailBase, "badtype");
        const { ok, status, json } = await intakePqrsd({
          type: "info",
          email,
          subject: `${LAB_TAG} tipo no permitido ${Date.now()}`,
          files: [makeDisallowedFile()],
        });
        if (ok && json?.radicado) ctx.register(json.radicado);
        return {
          assertions: [
            assert(
              "rechazado (4xx / result:false)",
              !ok || json?.result === false,
              `HTTP ${status} ${json?.error || ""}`
            ),
          ],
        };
      },
    },
    {
      key: "idempotency",
      name: "Idempotencia (mismo X-Idempotency-Key)",
      description: "Reenviar con la misma clave devuelve el mismo radicado (replay).",
      run: async (ctx) => {
        const key = uuid();
        const email = aliasEmail(ctx.testEmailBase, "idem");
        const subject = `${LAB_TAG} idempotencia ${Date.now()}`;
        const first = await intakePqrsd({ type: "queja", email, subject, idempotencyKey: key });
        if (first.ok && first.json?.radicado) ctx.register(first.json.radicado);
        const second = await intakePqrsd({ type: "queja", email, subject, idempotencyKey: key });
        return {
          assertions: [
            assert("1ra radicación ok", first.ok && !!first.json?.radicado, first.json?.radicado),
            assert("2da marca replay", second.json?.replay === true, `replay=${second.json?.replay}`),
            assertEqual("mismo radicado", second.json?.radicado, first.json?.radicado),
          ],
        };
      },
    },
    {
      key: "rate-limit",
      name: "Límite de tasa por correo",
      description: "6 envíos con el mismo correo en la ventana disparan un 429.",
      run: async (ctx) => {
        const email = aliasEmail(ctx.testEmailBase, "rate");
        let limited = false;
        for (let i = 0; i < 6; i++) {
          const r = await intakePqrsd({
            type: "sugerencia",
            email,
            subject: `${LAB_TAG} rate ${i}`,
            authenticated: false,
          });
          if (r.ok && r.json?.radicado) ctx.register(r.json.radicado);
          if (r.status === 429 || (r.json?.result === false && /rate|tasa|too many/i.test(r.json?.error || ""))) {
            limited = true;
          }
        }
        return {
          assertions: [
            assert("se observó un 429 / rate limit", limited, "tope = 5/correo/10min"),
          ],
          message: "Ejecutar al final: agota el cupo del correo a propósito.",
        };
      },
    },
    {
      key: "form-validation",
      name: "Validación de campos del formulario",
      description: "Reglas de ID por tipo de solicitante (numérico vs alfanumérico).",
      run: async () => ({
        status: "skipped",
        message:
          "MANUAL: los validadores viven dentro de PqrsdForm (no exportados). Verificar en /pqrsd/radicar dejando campos requeridos vacíos y con un ID inválido.",
      }),
    },
  ],
};

// ===========================================================================
// SUITE C — Admin lifecycle (live)
// ===========================================================================
const suiteLifecycle = {
  key: "lifecycle",
  title: "C · Ciclo de vida administrativo (backend en vivo)",
  tests: [
    {
      key: "fixture",
      name: "Crear PQRSD (fixture del ciclo)",
      description: "Radica una queja y confirma estado inicial 'radicado'.",
      run: async (ctx) => {
        const fx = await createFixture(ctx, { type: "queja", tag: "life" });
        const docu = await getPqrsdRetry(fx.radicado);
        ctx.state.life = { radicado: fx.radicado, prevDeadline: docu?.pqrsd?.slaDeadline || null };
        return {
          assertions: [
            assert("documento existe", !!docu, fx.radicado),
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.RADICADO),
          ],
        };
      },
    },
    {
      key: "assign",
      name: "Asignar",
      description: "assignPqrsd → estado 'asignado' + datos del responsable.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture (falló el paso anterior)." };
        await assignPqrsd(rad, selfTarget(ctx.user), actorOf(ctx.user));
        const docu = await getPqrsd(rad);
        return {
          assertions: [
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.ASIGNADO),
            assertEqual("assignee", docu?.pqrsd?.assignee, ctx.user?.userId),
          ],
        };
      },
    },
    {
      key: "in-progress",
      name: "En trámite",
      description: "setPqrsdInProgress → estado 'en_tramite'.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };
        await setPqrsdInProgress(rad);
        const docu = await getPqrsd(rad);
        return { assertions: [assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.EN_TRAMITE)] };
      },
    },
    {
      key: "forward",
      name: "Reenviar (forward)",
      description: "forwardPqrsd → sigue 'en_tramite' + evento PQRSD_FORWARDED.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };
        await forwardPqrsd(rad, selfTarget(ctx.user), "Reenvío de prueba laboratorio", actorOf(ctx.user));
        const docu = await getPqrsd(rad);
        return {
          assertions: [
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.EN_TRAMITE),
            assert("evento PQRSD_FORWARDED", eventTypes(docu).includes("PQRSD_FORWARDED")),
          ],
        };
      },
    },
    {
      key: "return",
      name: "Devolver (return)",
      description: "returnPqrsd → 'devuelto', sin responsable, returnCount++.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };
        await returnPqrsd(rad, "Devolución de prueba del laboratorio", actorOf(ctx.user));
        const docu = await getPqrsd(rad);
        ctx.state.life.prevDeadline = docu?.pqrsd?.slaDeadline || ctx.state.life.prevDeadline;
        return {
          assertions: [
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.DEVUELTO),
            assertEqual("assignee liberado", docu?.pqrsd?.assignee, null),
            assert("returnCount ≥ 1", (docu?.pqrsd?.returnCount || 0) >= 1, `count=${docu?.pqrsd?.returnCount}`),
            assert("evento PQRSD_RETURNED", eventTypes(docu).includes("PQRSD_RETURNED")),
          ],
        };
      },
    },
    {
      key: "return-validation",
      name: "Validación de devolución (<10 caracteres)",
      description: "returnPqrsd con motivo corto debe rechazar.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };
        return {
          assertions: [
            await assertThrowsAsync(
              "motivo corto rechazado",
              () => returnPqrsd(rad, "corto", actorOf(ctx.user)),
              /mínimo|minimo|10/
            ),
          ],
        };
      },
    },
    {
      key: "reassign",
      name: "Reasignar (reinicia SLA)",
      description: "reassignPqrsd → 'asignado', slaResetCount++, slaBaseDate y slaDeadline reiniciados.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };

        // Snapshot SLA state BEFORE the reset so we can prove it changed. The
        // server computes the original deadline with Colombian holidays while
        // reassignPqrsd uses a weekends-only client calc, so a strict
        // "new >= old" comparison is unreliable — we assert the reset semantics
        // (counter advanced, base date refreshed, deadline is a new future
        // value) instead of an ordering between the two calendars.
        const before = await getPqrsd(rad);
        const oldDeadline = before?.pqrsd?.slaDeadline || null;
        const oldCount = before?.pqrsd?.slaResetCount || 0;

        const res = await reassignPqrsd(rad, selfTarget(ctx.user), actorOf(ctx.user));
        const docu = await getPqrsd(rad);
        const newDeadline = docu?.pqrsd?.slaDeadline;
        const newCount = docu?.pqrsd?.slaResetCount || 0;
        const baseDate = docu?.pqrsd?.slaBaseDate;
        const now = Date.now();

        return {
          assertions: [
            assert("retorna newDeadline", !!res?.newDeadline, String(res?.newDeadline)),
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.ASIGNADO),
            assert("slaResetCount incrementó", newCount > oldCount, `${oldCount} → ${newCount}`),
            assert("slaBaseDate ≈ ahora", baseDate && Math.abs(new Date(baseDate).getTime() - now) < 5 * 60 * 1000, baseDate),
            assert("nuevo plazo en el futuro", newDeadline && new Date(newDeadline).getTime() > now, newDeadline),
            assert("plazo reiniciado (distinto al anterior)", newDeadline !== oldDeadline, `${oldDeadline} → ${newDeadline}`),
            assert("slaBreached limpiado", docu?.pqrsd?.slaBreached === false || docu?.pqrsd?.slaBreached == null),
            assert("evento PQRSD_SLA_RESET", eventTypes(docu).includes("PQRSD_SLA_RESET")),
            assert("evento PQRSD_REASSIGNED", eventTypes(docu).includes("PQRSD_REASSIGNED")),
          ],
        };
      },
    },
    {
      key: "close",
      name: "Cerrar",
      description: "closePqrsd → estado 'cerrado' + closedAt.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture." };
        await closePqrsd(rad);
        const docu = await getPqrsd(rad);
        return {
          assertions: [
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.CERRADO),
            assert("closedAt sellado", !!docu?.pqrsd?.closedAt, docu?.pqrsd?.closedAt),
          ],
        };
      },
    },
    {
      key: "logs",
      name: "Bitácora de eventos (logs)",
      description:
        "events[] registra cada acción del ciclo con estructura correcta e IDs únicos.",
      run: async (ctx) => {
        const rad = ctx.state.life?.radicado;
        if (!rad) return { status: "skipped", message: "Sin fixture del ciclo." };
        const docu = await getPqrsd(rad);
        const events = Array.isArray(docu?.events) ? docu.events : [];
        const types = events.map((e) => e.eventType);
        const ids = events.map((e) => e.eventId);
        const wellFormed = events.every(
          (e) => e.eventType && e.eventId && (e.at || e.fecha) && (e.byUserId || e.porUserId)
        );
        const uniqueIds = new Set(ids).size === ids.length;
        return {
          assertions: [
            assert("hay eventos registrados", events.length >= 4, `n=${events.length}`),
            assert("registra reenvío (PQRSD_FORWARDED)", types.includes("PQRSD_FORWARDED")),
            assert("registra devolución (PQRSD_RETURNED)", types.includes("PQRSD_RETURNED")),
            assert("registra reasignación (PQRSD_REASSIGNED)", types.includes("PQRSD_REASSIGNED")),
            assert("registra reinicio SLA (PQRSD_SLA_RESET)", types.includes("PQRSD_SLA_RESET")),
            assert("cada evento bien formado (tipo, id, fecha, actor)", wellFormed),
            assert("eventIds únicos (sin duplicados)", uniqueIds, `${ids.length} ids`),
          ],
          message: `Tipos: ${Array.from(new Set(types)).join(", ")}`,
          data: { events },
        };
      },
    },
    {
      key: "multi-assignee",
      name: "Asignación múltiple (multi-operario)",
      description:
        "assignPqrsd con varios responsables → assignees[]/assigneeIds[], dedupe y mirror legacy.",
      run: async (ctx) => {
        const fx = await createFixture(ctx, { type: "queja", tag: "multi" });
        ctx.state.multiRadicado = fx.radicado;
        const internal = await getInternalUsers(ctx.user);
        const self = selfTarget(ctx.user);
        const second = internal.find((u) => u.userId && u.userId !== ctx.user?.userId);
        const secondTarget = second
          ? {
              userId: second.userId,
              name: `${second.name || ""} ${second.lastName || ""}`.trim(),
              email: second.email || null,
            }
          : null;
        // Pass a duplicate of self to exercise dedupe, plus a real 2nd operator
        // if the environment has one (avoids creating an orphan task).
        const input = secondTarget ? [self, self, secondTarget] : [self, self];
        const expected = secondTarget ? 2 : 1;
        await assignPqrsd(fx.radicado, input, actorOf(ctx.user));
        const docu = await getPqrsd(fx.radicado);
        const assignees = docu?.pqrsd?.assignees || [];
        const ids = docu?.pqrsd?.assigneeIds || [];
        return {
          assertions: [
            assertEqual("assignees deduplicados", assignees.length, expected),
            assertEqual("assigneeIds = assignees", ids.length, expected),
            assert("incluye al tester", ids.includes(ctx.user?.userId), ids.join(",")),
            secondTarget
              ? assert("incluye 2do operario", ids.includes(secondTarget.userId))
              : assert("dedupe verificado (1 solo operario interno disponible)", true),
            assertEqual("mirror legacy assignee = primario", docu?.pqrsd?.assignee, ctx.user?.userId),
            assertEqual("estado", docu?.pqrsd?.status, PQRSD_STATUS.ASIGNADO),
          ],
          message: secondTarget
            ? `Asignada a ${expected} operarios.`
            : "Solo el tester disponible como operario interno; se verificó el dedupe.",
          data: { assignees, assigneeIds: ids, assigneeLegacy: docu?.pqrsd?.assignee },
        };
      },
    },
  ],
};

// ===========================================================================
// SUITE D — List / search / pagination (live)
// ===========================================================================
const suiteList = {
  key: "list",
  title: "D · Listado, búsqueda y paginación (backend en vivo)",
  tests: [
    {
      key: "list",
      name: "listPqrsd devuelve arreglo",
      description: "Lista rápida con tope; cada fila tiene filedId.",
      run: async () => {
        const list = await listPqrsd({ limit: 5 });
        return {
          assertions: [
            assert("es arreglo", Array.isArray(list)),
            assert("todas con filedId", list.every((r) => !!r.filedId), `n=${list.length}`),
          ],
        };
      },
    },
    {
      key: "paginate",
      name: "Paginación por cursor (sin solapamiento)",
      description: "Página 1 → página 2 con cursor: conjuntos disjuntos.",
      run: async () => {
        const p1 = await fetchPqrsdPage({ pageSize: 3 });
        const assertions = [
          assert("items ≤ pageSize", p1.items.length <= 3, `n=${p1.items.length}`),
          assert("hasMore es boolean", typeof p1.hasMore === "boolean"),
        ];
        if (p1.hasMore && p1.lastDoc) {
          const p2 = await fetchPqrsdPage({ pageSize: 3, cursor: p1.lastDoc });
          const ids1 = p1.items.map((r) => r.filedId);
          const overlap = p2.items.filter((r) => ids1.includes(r.filedId));
          assertions.push(assert("sin solapamiento p1/p2", overlap.length === 0, `solapados=${overlap.length}`));
        } else {
          assertions.push(assert("una sola página (sin más datos)", true, "hasMore=false"));
        }
        return { assertions };
      },
    },
    {
      key: "filter-status",
      name: "Filtro por estado",
      description: "fetchPqrsdPage({status}) → todas las filas con ese estado.",
      run: async () => {
        const page = await fetchPqrsdPage({ status: PQRSD_STATUS.CERRADO, pageSize: 5 });
        return {
          assertions: [
            assert(
              "todas 'cerrado' (o vacío)",
              page.items.every((r) => r.pqrsd?.status === PQRSD_STATUS.CERRADO),
              `n=${page.items.length}`
            ),
          ],
        };
      },
    },
    {
      key: "filter-date",
      name: "Filtro por rango de fecha",
      description: "createdAt acotado entre dateFrom y dateTo.",
      run: async () => {
        const dateFrom = new Date(Date.now() - 1000 * 60 * 60 * 24 * 3);
        const dateTo = new Date(Date.now() + 1000 * 60 * 60 * 24);
        const page = await fetchPqrsdPage({ dateFrom, dateTo, pageSize: 5 });
        const inRange = page.items.every((r) => {
          const t = new Date(r.createdAt).getTime();
          return t >= dateFrom.getTime() && t <= dateTo.getTime();
        });
        return { assertions: [assert("createdAt dentro del rango", inRange, `n=${page.items.length}`)] };
      },
    },
    {
      key: "subscribe",
      name: "subscribePqrsdList (tiempo real)",
      description: "El listener emite un arreglo y se cancela con unsubscribe.",
      run: async () => {
        const result = await new Promise((resolve) => {
          let done = false;
          const unsub = subscribePqrsdList({}, (items) => {
            if (done) return;
            done = true;
            resolve({ items, unsub });
          });
          setTimeout(() => {
            if (!done) {
              done = true;
              resolve({ items: null, unsub });
            }
          }, 6000);
        });
        if (typeof result.unsub === "function") result.unsub();
        return {
          assertions: [
            assert("callback emitió arreglo", Array.isArray(result.items), result.items ? `n=${result.items.length}` : "timeout"),
          ],
        };
      },
    },
    {
      key: "client-search",
      name: "Búsqueda cliente (predicado asunto/tipo)",
      description: "Replica el filtro client-side sobre filas ya cargadas.",
      run: async () => {
        const list = await listPqrsd({ limit: 10 });
        if (!list.length) return { status: "skipped", message: "No hay PQRSD para filtrar." };
        const sample = list[0];
        const term = String(sample.subject || sample.pqrsd?.type || "").slice(0, 6).toLowerCase();
        const predicate = (r) =>
          [r.subject, r.pqrsd?.type].join(" ").toLowerCase().includes(term);
        const matches = list.filter(predicate);
        return {
          assertions: [
            assert("término no vacío", term.length > 0, `"${term}"`),
            assert("encuentra ≥ 1 coincidencia", matches.length >= 1, `n=${matches.length}`),
          ],
        };
      },
    },
    {
      key: "mine-scope",
      name: "Filtro 'Mis PQRSDs' (assigneeContains)",
      description:
        "fetchPqrsdPage({assigneeContains}) usa array-contains sobre assigneeIds[] (multi-operario).",
      run: async (ctx) => {
        if (!ctx.user?.userId) return { status: "skipped", message: "Sin userId." };
        // Reuse a fixture where the tester is already an assignee (the lifecycle
        // PQRSD ends with the tester in assigneeIds after reassign), or the
        // multi-assignee fixture — no extra intake needed.
        const rad = ctx.state.life?.radicado || ctx.state.multiRadicado;
        if (!rad) return { status: "skipped", message: "Sin fixture asignado al tester." };
        const page = await fetchPqrsdPage({ assigneeContains: ctx.user.userId, pageSize: 25 });
        const found = page.items.some((r) => r.filedId === rad);
        const allMine = page.items.every((r) =>
          (r.pqrsd?.assigneeIds || []).includes(ctx.user.userId)
        );
        return {
          assertions: [
            assert("incluye una PQRSD del tester", found, rad),
            assert("todas contienen al operario en assigneeIds", allMine, `n=${page.items.length}`),
          ],
        };
      },
    },
  ],
};

// ===========================================================================
// SUITE E — Public tracking (live)
// ===========================================================================
const suiteTracking = {
  key: "tracking",
  title: "E · Rastreo público ciudadano (backend en vivo)",
  tests: [
    {
      key: "track",
      name: "Rastreo por radicado + correo",
      description: "handlePqrsdPublic devuelve la proyección de la PQRSD.",
      run: async (ctx) => {
        const fx = await createFixture(ctx, { type: "peticion_general", tag: "track" });
        ctx.state.track = fx;
        const r = await callPqrsdPublic({ radicado: fx.radicado, email: fx.email });
        return {
          assertions: [
            assert("responde ok", r.ok && r.json?.result !== false, `HTTP ${r.status}`),
            assertEqual("radicado coincide", r.json?.pqrsd?.radicado, fx.radicado),
            assert("incluye asunto", !!r.json?.pqrsd?.subject, r.json?.pqrsd?.subject),
          ],
        };
      },
    },
    {
      key: "wrong-email",
      name: "Correo incorrecto → no encontrado",
      description: "El rastreo con correo equivocado no expone la PQRSD.",
      run: async (ctx) => {
        const fx = ctx.state.track;
        if (!fx) return { status: "skipped", message: "Sin fixture de rastreo." };
        const r = await callPqrsdPublic({ radicado: fx.radicado, email: "noexiste+x@example.com" });
        return {
          assertions: [
            assert(
              "rechazado (404 / result:false)",
              !r.ok || r.status === 404 || r.json?.result === false,
              `HTTP ${r.status}`
            ),
          ],
        };
      },
    },
    {
      key: "reference",
      name: "Rastreo por referenceId",
      description: "El código de referencia cifrado resuelve la misma PQRSD.",
      run: async (ctx) => {
        const fx = ctx.state.track;
        if (!fx?.referenceId) return { status: "skipped", message: "Intake no devolvió referenceId." };
        const r = await callPqrsdPublic({ referenceId: fx.referenceId, email: fx.email });
        return {
          assertions: [
            assert("responde ok", r.ok && r.json?.result !== false, `HTTP ${r.status}`),
            assertEqual("radicado coincide", r.json?.pqrsd?.radicado, fx.radicado),
          ],
        };
      },
    },
    {
      key: "citizen-actions",
      name: "Enlace mágico y acciones ciudadanas",
      description: "Confirmar / pedir más info tras una respuesta FINAL.",
      run: async () => ({
        status: "skipped",
        message:
          "MANUAL: el token JWT se envía por correo y las acciones requieren estado 'respondido'. Verificar desde el enlace mágico del correo de respuesta en /pqrsd/estado.",
      }),
    },
  ],
};

// ===========================================================================
// SUITE F — Response flow (partial)
// ===========================================================================
// The answer templates the start dialog offers, and the shortest chain the
// server accepts, with the tester on both ends.
const labAnswerTemplates = async () => availableAnswerTemplates((await getPqrsdConfig())?.documents);
const labChain = (user) => [
  { type: "elaborate", userId: user.userId },
  { type: "sign", userId: user.userId },
];

const suiteFlow = {
  key: "flow",
  title: "F · Flujo de respuesta supervisado (parcial)",
  tests: [
    {
      key: "templates",
      name: "Plantillas de respuesta",
      description: "Config → PQRSD → Documentos ofrece plantillas de respuesta activas y generadas.",
      run: async (ctx) => {
        const templates = await labAnswerTemplates();
        ctx.state.templates = Array.isArray(templates) ? templates : [];
        return {
          assertions: [
            assert("es arreglo", Array.isArray(templates), `n=${ctx.state.templates.length}`),
          ],
          message: ctx.state.templates.length
            ? `${ctx.state.templates.length} plantilla(s) disponibles`
            : "No hay plantillas de respuesta activas en este ambiente.",
        };
      },
    },
    {
      key: "start-flow",
      name: "Iniciar flujo de respuesta (interino)",
      description: "startResponseFlow enlaza el flujo al PQRSD padre.",
      run: async (ctx) => {
        if (!ctx.perms.includes(PERMISSIONS.PQRSD.START_RESPONSE_FLOW)) {
          return { status: "skipped", message: "El usuario no tiene pqrsd.start_response_flow." };
        }
        const templates = ctx.state.templates || (await labAnswerTemplates());
        // First available answer template, and the shortest chain: the tester
        // elaborates and signs.
        const tpl = templates && templates[0];
        if (!tpl) return { status: "skipped", message: "No hay plantilla de respuesta para iniciar." };
        const steps = labChain(ctx.user);

        const fx = await createFixture(ctx, { type: "reclamo", tag: "flow" });
        await assignPqrsd(fx.radicado, selfTarget(ctx.user), actorOf(ctx.user));
        const res = await startResponseFlow(
          {
            parentRadicadoId: fx.radicado,
            answerTemplateId: tpl.id,
            steps,
            subject: `${LAB_TAG} respuesta ${fx.radicado}`,
            responseType: "interim",
          },
          ctx.user
        );
        const docu = await getPqrsd(fx.radicado);
        return {
          assertions: [
            assert("plantilla seleccionada", !!tpl.id, tpl.name),
            assert("retorna flowId", !!res?.flowId, res?.flowId),
            assertEqual(
              "padre in_progress",
              docu?.pqrsd?.currentResponseFlowStatus,
              "in_progress"
            ),
            assert(
              "evento RESPONSE_FLOW_STARTED",
              eventTypes(docu).includes("RESPONSE_FLOW_STARTED")
            ),
          ],
        };
      },
    },
    {
      key: "concurrent-flows",
      name: "Flujos de respuesta concurrentes (múltiples respuestas)",
      description:
        "Dos flujos interinos simultáneos sobre la misma PQRSD se registran en activeResponseFlows[].",
      run: async (ctx) => {
        if (!ctx.perms.includes(PERMISSIONS.PQRSD.START_RESPONSE_FLOW)) {
          return { status: "skipped", message: "El usuario no tiene pqrsd.start_response_flow." };
        }
        const templates = ctx.state.templates || (await labAnswerTemplates());
        const tpl = templates && templates[0];
        if (!tpl) return { status: "skipped", message: "No hay plantilla de respuesta." };
        const steps = labChain(ctx.user);

        const fx = await createFixture(ctx, { type: "peticion_general", tag: "concflow" });
        await assignPqrsd(fx.radicado, selfTarget(ctx.user), actorOf(ctx.user));

        const start = (n) =>
          startResponseFlow(
            {
              parentRadicadoId: fx.radicado,
              answerTemplateId: tpl.id,
              steps,
              subject: `${LAB_TAG} resp ${n} ${fx.radicado}`,
              responseType: "interim",
            },
            ctx.user
          );

        const f1 = await start(1);
        let f2 = null;
        let err2 = null;
        try {
          f2 = await start(2);
        } catch (e) {
          err2 = e?.message || String(e);
        }
        const docu = await getPqrsd(fx.radicado);
        const active = docu?.pqrsd?.activeResponseFlows || [];
        return {
          assertions: [
            assert("1er flujo inició", !!f1?.flowId, f1?.flowId),
            assert("2do flujo concurrente inició", !!f2?.flowId, f2?.flowId || err2),
            assert("activeResponseFlows ≥ 2", active.length >= 2, `n=${active.length}`),
          ],
          message: `Tope interinos por ciclo: ${PQRSD_MAX_INTERIMS_PER_CYCLE}.`,
          data: { activeResponseFlows: active, flow1: f1, flow2: f2 || err2 },
        };
      },
    },
    {
      key: "flow-completion",
      name: "Completar flujo / emisión / tope interinos",
      description: "Aprobación, firma y publicación de la respuesta.",
      run: async () => ({
        status: "skipped",
        message:
          "MANUAL: la finalización requiere la firma en RoyalEditorApi y los triggers Firestore (pqrsdResponseFlowSync / pqrsdOnFiledDone). Verificar editando y firmando el flujo en el editor.",
      }),
    },
  ],
};

// ===========================================================================
// SUITE G — SLA
// ===========================================================================
const suiteSla = {
  key: "sla",
  title: "G · SLA (plazos y barrido)",
  tests: [
    {
      key: "deadline",
      name: "Plazo SLA calculado al radicar",
      description: "slaDeadline queda en el futuro dentro de la ventana del tipo.",
      run: async (ctx) => {
        const fx = await createFixture(ctx, { type: "info", tag: "sla" });
        const docu = await getPqrsdRetry(fx.radicado);
        const dl = docu?.pqrsd?.slaDeadline ? new Date(docu.pqrsd.slaDeadline) : null;
        const now = Date.now();
        const maxWindow = now + 1000 * 60 * 60 * 24 * 40; // 10 hábiles ≈ 2 sem; tope holgado
        return {
          assertions: [
            assert("slaDeadline definido", !!dl, String(docu?.pqrsd?.slaDeadline)),
            assert("plazo en el futuro", dl && dl.getTime() > now),
            assert("plazo dentro de ventana razonable", dl && dl.getTime() < maxWindow),
          ],
        };
      },
    },
    {
      key: "sweep",
      name: "Barrido SLA (aviso 80% / vencimiento)",
      description: "Correos de aviso y vencimiento del job programado.",
      run: async () => ({
        status: "skipped",
        message:
          "MANUAL: pqrsdSlaSweep es un job programado (06:00 Bogotá) que envía correos. Verificar disparando el endpoint HTTP manual en un entorno controlado.",
      }),
    },
  ],
};


// ===========================================================================
// SUITE H — Requerimiento de información al ciudadano (CPACA art. 17)
// ===========================================================================
//
// Exercises the whole suspension cycle against the live dev backend: the
// officer requires, the citizen answers through the scoped link, and the term
// comes back with the business days that were left. The citizen half is driven
// with the link the send response returns — the same one that goes out by mail.

// Files a PQRSD, puts it in trámite and sends a two-item requerimiento.
// Returns { fx, requestId, replyLink, token, before } where `before` is the
// pqrsd map as it looked before the suspension.
async function requireInfo(ctx, tag, { remainingBusinessDays } = {}) {
  const fx = await createFixture(ctx, { type: "peticion_general", tag });
  await getPqrsdRetry(fx.radicado);
  await setPqrsdInProgress(fx.radicado, actorOf(ctx.user));
  // Optionally burn most of the term first, so a later assertion can prove the
  // reply RESUMED what was left instead of handing out a fresh window.
  if (remainingBusinessDays != null) {
    await forceSlaDeadline(fx.radicado, addBusinessDays(new Date(), remainingBusinessDays));
  }
  const before = (await getPqrsdRetry(fx.radicado))?.pqrsd || {};
  const out = await sendInfoRequest(
    fx.radicado,
    {
      items: [
        { kind: "question", header: "¿Cuál es la dirección exacta del predio?", required: true },
        { kind: "file", label: "Cédula", help: "Ambas caras en un solo PDF", required: true },
      ],
      note: `${LAB_TAG} requerimiento de prueba`,
    },
    ctx.user
  );
  return {
    fx,
    requestId: out.requestId,
    replyLink: out.replyLink,
    token: tokenFromLink(out.replyLink),
    before,
  };
}

const suiteInfoRequest = {
  key: "info_request",
  title: "H · Requerimiento de información (art. 17)",
  tests: [
    {
      key: "send_suspends",
      name: "El requerimiento suspende el término",
      description:
        "Radica el requerimiento como comunicación de salida, pasa a Pendiente de información y congela el plazo.",
      run: async (ctx) => {
        // 4 business days left of the 15 the type grants: enough to tell a
        // resumed term from a reset one further down the suite.
        const { fx, requestId, replyLink } = await requireInfo(ctx, "req", { remainingBusinessDays: 4 });
        ctx.state.infoFixture = { ...fx, requestId, replyLink };
        const docu = await getPqrsdRetry(fx.radicado);
        const p = docu?.pqrsd || {};
        const types = eventTypes(docu);
        const requests = await listInfoRequests(fx.radicado);
        const sent = requests.find((r) => r.id === requestId);
        return {
          assertions: [
            assertEqual("estado", p.status, PQRSD_STATUS.PENDIENTE_INFO),
            assert("slaPaused", p.slaPaused === true, String(p.slaPaused)),
            assert(
              "guarda los días hábiles restantes",
              Number(p.slaRemainingBusinessDays) > 0,
              String(p.slaRemainingBusinessDays)
            ),
            assert("plazo del ciudadano fijado", !!p.citizenDeadline, String(p.citizenDeadline)),
            assert("requerimiento activo apuntado", p.activeInfoRequestId === requestId),
            assertEqual("contador de requerimientos", p.infoRequestCount, 1),
            assert("evento PQRSD_INFO_REQUESTED", types.includes("PQRSD_INFO_REQUESTED")),
            assert("evento PQRSD_SLA_PAUSED", types.includes("PQRSD_SLA_PAUSED")),
            assert("radicado de salida del requerimiento", !!sent?.requerimientoFiledId, sent?.requerimientoFiledId),
            assert("enlace seguro para el ciudadano", !!replyLink && replyLink.includes("t=")),
            assert("2 ítems solicitados", (sent?.items || []).length === 2),
          ],
        };
      },
    },
    {
      key: "not_a_response",
      name: "El requerimiento no cuenta como respuesta",
      description:
        "No aparece en el historial de respuestas del ciudadano ni marca la PQRSD como respondida.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el test anterior." };
        const docu = await getPqrsdRetry(fx.radicado);
        const { json } = await callPqrsdPublic({ radicado: fx.radicado, email: fx.email });
        return {
          assertions: [
            assertEqual("responses[] sigue vacío", getPqrsdResponses(docu).length, 0),
            assert("no quedó respondida", docu?.pqrsd?.status !== PQRSD_STATUS.RESPONDIDO),
            assert(
              "el ciudadano no ve una respuesta",
              (json?.responses || []).length === 0,
              JSON.stringify(json?.responses || [])
            ),
            assert("el seguimiento anuncia el requerimiento", !!json?.pqrsd?.pendingInfoRequest),
          ],
        };
      },
    },
    {
      key: "cap",
      name: "Tope de requerimientos por PQRSD",
      description: "Con el tope en 1, un segundo requerimiento se rechaza.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el primer test." };
        return {
          assertions: [
            await assertThrowsAsync(
              "segundo requerimiento rechazado",
              () =>
                sendInfoRequest(
                  fx.radicado,
                  { items: [{ kind: "question", header: "¿Otra pregunta más?" }] },
                  ctx.user
                ),
              /requerimiento|máximo|curso/i
            ),
          ],
        };
      },
    },
    {
      key: "link_scope",
      name: "El enlace del requerimiento no cierra ni reabre",
      description:
        "Un token con propósito info_reply es rechazado por las acciones ciudadanas de handlePqrsdPublic.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el primer test." };
        const token = tokenFromLink(fx.replyLink);
        const confirm = await callPqrsdPublic({ token, action: "confirm" });
        const more = await callPqrsdPublic({ token, action: "request_more", reason: "prueba de alcance" });
        return {
          assertions: [
            assert("confirmar rechazado", !confirm.ok, `HTTP ${confirm.status}`),
            assert("reabrir rechazado", !more.ok, `HTTP ${more.status}`),
          ],
        };
      },
    },
    {
      key: "required_items",
      name: "No se puede enviar sin los ítems obligatorios",
      description: "El envío incompleto devuelve 400 y la lista de ítems faltantes.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el primer test." };
        const token = tokenFromLink(fx.replyLink);
        const { ok, status, json } = await callPqrsdInfoReply({ token, action: "submit", answers: {} });
        return {
          assertions: [
            assert("rechazado", !ok, `HTTP ${status}`),
            assert("informa los faltantes", Array.isArray(json?.missing) && json.missing.length === 2,
              JSON.stringify(json?.missing)),
            assert("mensaje en español", /Falta completar/i.test(json?.error || ""), json?.error),
          ],
        };
      },
    },
    {
      key: "load_and_draft",
      name: "El ciudadano carga el formulario y guarda borrador",
      description: "load devuelve los ítems; save_draft conserva lo escrito sin reanudar el término.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el primer test." };
        const token = tokenFromLink(fx.replyLink);
        const loaded = await callPqrsdInfoReply({ token, action: "load" });
        const draft = await callPqrsdInfoReply({
          token,
          action: "save_draft",
          answers: { it_1: "Calle 10 # 20-30, barrio La Paz" },
        });
        const docu = await getPqrsdRetry(fx.radicado);
        return {
          assertions: [
            assert("carga el requerimiento", loaded.ok && (loaded.json?.request?.items || []).length === 2),
            assert("no expone datos internos", !("tokenId" in (loaded.json?.request || {}))),
            assert("guarda el borrador", draft.ok && draft.json?.saved === true),
            assert("el borrador NO reanuda el término", docu?.pqrsd?.slaPaused === true),
            assertEqual("sigue pendiente", docu?.pqrsd?.status, PQRSD_STATUS.PENDIENTE_INFO),
          ],
        };
      },
    },
    {
      key: "reply_resumes",
      name: "La respuesta del ciudadano reanuda el término",
      description:
        "Con todos los ítems atendidos, la PQRSD vuelve a trámite con los días hábiles que quedaban.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el primer test." };
        const token = tokenFromLink(fx.replyLink);
        const before = (await getPqrsdRetry(fx.radicado))?.pqrsd || {};
        const remaining = Number(before.slaRemainingBusinessDays);
        const fullWindow = PQRSD_SLA_DAYS.peticion_general;
        const { ok, status, json } = await callPqrsdInfoReply({
          token,
          action: "submit",
          answers: { it_1: "Calle 10 # 20-30, barrio La Paz" },
          files: { it_2: makeTestFile("cedula.txt") },
        });
        const docu = await getPqrsdRetry(fx.radicado);
        const p = docu?.pqrsd || {};
        const types = eventTypes(docu);
        const requests = await listInfoRequests(fx.radicado);
        const answered = requests.find((r) => r.id === fx.requestId);
        const fileItem = (answered?.items || []).find((i) => i.kind === "file");
        const deadline = p.slaDeadline ? new Date(p.slaDeadline) : null;
        const returned = deadline ? diffBusinessDays(new Date(), deadline) : null;
        return {
          assertions: [
            assert("envío aceptado", ok, `HTTP ${status} ${json?.error || ""}`),
            assert("vuelve a trámite", p.status === PQRSD_STATUS.EN_TRAMITE, p.status),
            assert("término reanudado", p.slaPaused === false, String(p.slaPaused)),
            assert("plazo nuevo en el futuro", deadline && deadline.getTime() > Date.now()),
            // The invariant of art. 17: the entity gets back the days it had
            // left, not a brand-new term. Compared in BUSINESS days (the unit
            // the SLA is expressed in) with a tolerance of one, because the
            // reactivated term is anchored at midnight of the next business
            // day rather than at the hour the citizen happened to answer.
            assert(
              "devuelve los días hábiles que quedaban",
              returned != null && Math.abs(returned - remaining) <= 1,
              `devueltos ${returned}, quedaban ${remaining}`
            ),
            assert(
              "no regala un plazo completo",
              remaining < fullWindow && returned != null && returned < fullWindow,
              `quedaban ${remaining} de ${fullWindow} hábiles, devueltos ${returned}`
            ),
            assert("requerimiento marcado como respondido", answered?.status === "replied", answered?.status),
            assert("archivo del ciudadano almacenado", !!fileItem?.file?.url, fileItem?.file?.name),
            assert("evento PQRSD_INFO_REPLIED", types.includes("PQRSD_INFO_REPLIED")),
            assert("evento PQRSD_SLA_RESUMED", types.includes("PQRSD_SLA_RESUMED")),
            assert("sin plazo del ciudadano pendiente", !p.citizenDeadline),
          ],
        };
      },
    },
    {
      key: "link_spent",
      name: "El enlace se agota al responder",
      description: "Un segundo envío con el mismo enlace es rechazado.",
      run: async (ctx) => {
        const fx = ctx.state.infoFixture;
        if (!fx) return { status: "skipped", message: "Requiere el test anterior." };
        const token = tokenFromLink(fx.replyLink);
        const { ok, status } = await callPqrsdInfoReply({
          token,
          action: "submit",
          answers: { it_1: "reintento" },
        });
        return {
          assertions: [assert("reenvío rechazado", !ok, `HTTP ${status}`)],
        };
      },
    },
    {
      key: "extension",
      name: "Prórroga a solicitud del ciudadano",
      description: "Amplía el plazo del ciudadano y solo se concede una vez.",
      run: async (ctx) => {
        const { fx, requestId, token } = await requireInfo(ctx, "prorroga");
        const first = await callPqrsdInfoReply({ token, action: "request_extension" });
        const second = await callPqrsdInfoReply({ token, action: "request_extension" });
        const requests = await listInfoRequests(fx.radicado);
        const req = requests.find((r) => r.id === requestId);
        const due = req?.dueAt ? new Date(req.dueAt) : null;
        ctx.state.infoExtended = { ...fx, requestId, token };
        return {
          assertions: [
            assert("primera prórroga concedida", first.ok, `HTTP ${first.status}`),
            assert("segunda prórroga rechazada", !second.ok, `HTTP ${second.status}`),
            assert(
              "el plazo se amplió más de 30 días",
              due && due.getTime() > Date.now() + 31 * 86400000,
              String(req?.dueAt)
            ),
          ],
        };
      },
    },
    {
      key: "desist_guard",
      name: "El desistimiento exige plazo vencido",
      description: "No se puede archivar mientras el ciudadano sigue en plazo.",
      run: async (ctx) => {
        const fx = ctx.state.infoExtended;
        if (!fx) return { status: "skipped", message: "Requiere el test de prórroga." };
        return {
          assertions: [
            await assertThrowsAsync(
              "decretar desistimiento rechazado",
              () =>
                decreeDesistimiento(
                  fx.radicado,
                  "Prueba de laboratorio: intento de archivo antes del vencimiento.",
                  ctx.user
                ),
              /venc|plazo/i
            ),
          ],
        };
      },
    },
    {
      key: "withdraw",
      name: "Retirar el requerimiento reanuda el término",
      description: "Deja sin efecto el requerimiento sin respuesta del ciudadano.",
      run: async (ctx) => {
        const { fx, requestId } = await requireInfo(ctx, "retiro");
        await withdrawInfoRequest(fx.radicado, requestId, `${LAB_TAG} retiro de prueba`, ctx.user);
        const docu = await getPqrsdRetry(fx.radicado);
        const p = docu?.pqrsd || {};
        const requests = await listInfoRequests(fx.radicado);
        const req = requests.find((r) => r.id === requestId);
        return {
          assertions: [
            assert("término reanudado", p.slaPaused === false, String(p.slaPaused)),
            assertEqual("vuelve a trámite", p.status, PQRSD_STATUS.EN_TRAMITE),
            assertEqual("requerimiento retirado", req?.status, "withdrawn"),
            assert("sin requerimiento activo", !p.activeInfoRequestId),
            assert("evento de retiro", eventTypes(docu).includes("PQRSD_INFO_REQUEST_WITHDRAWN")),
          ],
        };
      },
    },
    {
      key: "officer_extend",
      name: "El funcionario también puede conceder la prórroga",
      description: "extendInfoRequest amplía el plazo desde el lado interno.",
      run: async (ctx) => {
        const { fx, requestId } = await requireInfo(ctx, "extint");
        await extendInfoRequest(fx.radicado, requestId, ctx.user);
        const requests = await listInfoRequests(fx.radicado);
        const req = requests.find((r) => r.id === requestId);
        const due = req?.dueAt ? new Date(req.dueAt) : null;
        return {
          assertions: [
            assert("prórroga registrada", !!req?.extensionGrantedAt),
            assert("plazo ampliado", due && due.getTime() > Date.now() + 31 * 86400000, String(req?.dueAt)),
          ],
        };
      },
    },
    {
      key: "sweep_paused",
      name: "El barrido no reclama demora mientras está suspendido",
      description: "Requiere disparar pqrsdSlaSweep con su clave interna.",
      run: async () => ({
        status: "skipped",
        message:
          "MANUAL: POST a pqrsdSlaSweep con el header X-INTERNAL-KEY (PQRSD_SWEEP_HTTP_KEY). " +
          "Con una PQRSD en Pendiente de información y el plazo congelado en el pasado, la respuesta " +
          "debe traer warned/breached sin incrementar por ella; al retrasar citizenDeadline, un primer " +
          "barrido envía el recordatorio y el segundo no lo repite.",
      }),
    },
  ],
};

// Cleanup is intentionally NOT a suite: "Ejecutar todo" runs everything below
// and then PAUSES for confirmation before deleting, so created PQRSDs can be
// audited (and the MANUAL/skipped checks validated) first. The bench owns the
// deletion via cleanupLabPqrsd + a confirmation gate.
export const SUITES = [
  suiteConstants,
  suiteIntake,
  suiteLifecycle,
  suiteList,
  suiteTracking,
  suiteFlow,
  suiteSla,
  suiteInfoRequest,
];

// Permission hints the pre-flight surfaces before running.
export const SUITE_PERMS = {
  lifecycle: [
    PERMISSIONS.PQRSD.ASSIGN,
    PERMISSIONS.PQRSD.RETURN,
    PERMISSIONS.PQRSD.FORWARD,
    PERMISSIONS.PQRSD.CLOSE,
  ],
  flow: [PERMISSIONS.PQRSD.START_RESPONSE_FLOW],
  list: [PERMISSIONS.PQRSD.LIST],
  info_request: [PERMISSIONS.PQRSD.REQUEST_INFO, PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO],
};
